import * as React from "react";
import { ApiError } from "@eduviaos/shared";
import type {
  ApplyResult,
  CapabilityAiClient,
  CapabilityPromptContextWire,
  CapabilitySuggestion,
  TargetKind,
} from "@eduviaos/shared";
import type { DrawerErrorVariant, DrawerState } from "../components/AIAssistDrawer.js";

/**
 * Phase F-finale-4 Slice 6 / F-finale-5 Slice 5b — headless controller for the
 * {@link AIAssistDrawer}.
 *
 * The drawer is deliberately network-free: it renders a {@link DrawerState} and
 * routes intent back through callbacks. This controller owns that state machine,
 * the prompt-context assembly seam (`buildContext`), and the BE error →
 * drawer-variant mapping. Apply success hands the {@link ApplyResult} to
 * `onApplied` so the host can invalidate its server-draft query.
 *
 * The {@link CapabilityAiClient} is injected, so the controller is portal-agnostic
 * and unit-testable without an `api` singleton. The super-admin portal builds a
 * cross-tenant (path-scoped) client; the institution-admin portal builds a
 * self-scoped (`/ai/capability`) client. Both delegate here.
 */

export interface DrawerErrorInfo {
  variant: DrawerErrorVariant;
  message: string;
  retryAfterSeconds?: number;
  /** 409 CAS conflict — the host's retry path should regenerate against a
   *  freshly-built context (new `draft_version`). */
  isConflict: boolean;
}

const CONFLICT_MESSAGE =
  "This draft changed since this suggestion was generated. " +
  "Regenerate to apply against the latest draft.";

const GENERIC_MESSAGE = "Something went wrong. Please try again.";

function readRetryAfter(details: Record<string, unknown> | undefined): number | undefined {
  const value = details?.["retry_after_seconds"];
  return typeof value === "number" ? value : undefined;
}

/**
 * Classify a thrown error into the drawer's error union. Keyed on the BE HTTP
 * status (the stable contract): 502 parse, 429 cost/rate, 503 unavailable,
 * 409 CAS conflict; anything else is generic.
 */
export function mapApiErrorToDrawerError(err: unknown): DrawerErrorInfo {
  if (err instanceof ApiError) {
    const retryAfterSeconds = readRetryAfter(err.details);
    switch (err.status) {
      case 502:
        return { variant: "parse", message: err.message, isConflict: false };
      case 429:
        return { variant: "cost", message: err.message, retryAfterSeconds, isConflict: false };
      case 503:
        return {
          variant: "unavailable",
          message: err.message,
          retryAfterSeconds,
          isConflict: false,
        };
      case 409:
        return { variant: "generic", message: CONFLICT_MESSAGE, isConflict: true };
      default:
        return { variant: "generic", message: err.message || GENERIC_MESSAGE, isConflict: false };
    }
  }
  return { variant: "generic", message: GENERIC_MESSAGE, isConflict: false };
}

function errorState(info: DrawerErrorInfo): DrawerState {
  return {
    status: "error",
    variant: info.variant,
    message: info.message,
    retryAfterSeconds: info.retryAfterSeconds,
  };
}

export interface CapabilityAiAssist {
  /** Whether the drawer is open. */
  open: boolean;
  /** Drawer `onOpenChange` target. */
  setOpen: (open: boolean) => void;
  /** Current drawer state machine value. */
  state: DrawerState;
  /** The most recent un-applied suggestion (for `LastSuggestionPanel`). */
  lastSuggestion: CapabilitySuggestion | null;
  /** Open a fresh prompt (resets any prior success/applied state). */
  start: () => void;
  /** Generate a suggestion from the prompt (drawer `onGenerate`). */
  generate: (prompt: string) => void;
  /** Apply the accepted nodes of the current suggestion (drawer `onApply`). */
  apply: (acceptedNodeIds: string[]) => void;
  /** Discard the current suggestion and close (drawer `onReject`). */
  reject: () => void;
  /** Re-run the last prompt against a freshly-built context (drawer `onRetry`). */
  retry: () => void;
  /** Re-open the drawer on the last suggestion (`LastSuggestionPanel` recall). */
  reopen: () => void;
}

export interface CapabilityAiAssistControllerOptions {
  client: CapabilityAiClient;
  targetKind: TargetKind;
  /** Assemble the prompt context fresh at request time (so `existing_stages` /
   *  `draft_version` reflect the latest server draft). */
  buildContext: () => CapabilityPromptContextWire;
  /** Invoked after a successful apply with the merge envelope. */
  onApplied?: (result: ApplyResult) => void;
}

/**
 * Client-injectable controller (testable without the `api` singleton). Portal
 * wrappers ({@link useSaCapabilityAiAssist} / `useIaCapabilityAiAssist`) build
 * the appropriately-scoped client and delegate here.
 */
export function useCapabilityAiAssistController({
  client,
  targetKind,
  buildContext,
  onApplied,
}: CapabilityAiAssistControllerOptions): CapabilityAiAssist {
  const [open, setOpen] = React.useState(false);
  const [state, setState] = React.useState<DrawerState>({ status: "idle" });
  const [lastSuggestion, setLastSuggestionState] = React.useState<CapabilitySuggestion | null>(
    null,
  );

  // Refs so callbacks stay stable and reads see the latest value without
  // re-creating the callback identities on every render.
  const reqIdRef = React.useRef(0);
  const currentSuggestionRef = React.useRef<CapabilitySuggestion | null>(null);
  const lastSuggestionRef = React.useRef<CapabilitySuggestion | null>(null);
  const lastPromptRef = React.useRef<string | null>(null);

  const setLastSuggestion = React.useCallback((s: CapabilitySuggestion | null) => {
    lastSuggestionRef.current = s;
    setLastSuggestionState(s);
  }, []);

  const runGenerate = React.useCallback(
    (prompt: string) => {
      lastPromptRef.current = prompt;
      const reqId = ++reqIdRef.current;
      setState({ status: "loading" });
      const context = buildContext();
      client
        .requestSuggestion({ target_kind: targetKind, user_prompt: prompt, context })
        .then((suggestion) => {
          if (reqId !== reqIdRef.current) return;
          currentSuggestionRef.current = suggestion;
          setLastSuggestion(suggestion);
          setState({ status: "success", suggestion });
        })
        .catch((err: unknown) => {
          if (reqId !== reqIdRef.current) return;
          setState(errorState(mapApiErrorToDrawerError(err)));
        });
    },
    [buildContext, client, setLastSuggestion, targetKind],
  );

  const apply = React.useCallback(
    (acceptedNodeIds: string[]) => {
      const suggestion = currentSuggestionRef.current;
      if (!suggestion) return;
      const reqId = ++reqIdRef.current;
      setState({ status: "applying", suggestion });
      client
        .applySuggestion(suggestion.id, {
          accepted_node_ids: acceptedNodeIds,
          draft_version: suggestion.draft_version_at_creation,
        })
        .then((result) => {
          if (reqId !== reqIdRef.current) return;
          currentSuggestionRef.current = null;
          setLastSuggestion(null);
          setState({ status: "applied", result });
          onApplied?.(result);
        })
        .catch((err: unknown) => {
          if (reqId !== reqIdRef.current) return;
          setState(errorState(mapApiErrorToDrawerError(err)));
        });
    },
    [client, onApplied, setLastSuggestion],
  );

  const reject = React.useCallback(() => {
    reqIdRef.current++; // invalidate any in-flight request
    currentSuggestionRef.current = null;
    setLastSuggestion(null);
    setState({ status: "idle" });
    setOpen(false);
  }, [setLastSuggestion]);

  const retry = React.useCallback(() => {
    const prompt = lastPromptRef.current;
    if (prompt == null) return;
    runGenerate(prompt);
  }, [runGenerate]);

  const reopen = React.useCallback(() => {
    const suggestion = lastSuggestionRef.current;
    if (!suggestion) return;
    currentSuggestionRef.current = suggestion;
    setState({ status: "success", suggestion });
    setOpen(true);
  }, []);

  const start = React.useCallback(() => {
    reqIdRef.current++; // invalidate any in-flight request
    currentSuggestionRef.current = null;
    setState({ status: "idle" });
    setOpen(true);
  }, []);

  return {
    open,
    setOpen,
    state,
    lastSuggestion,
    start,
    generate: runGenerate,
    apply,
    reject,
    retry,
    reopen,
  };
}
